// =============================================================================
// ProvidersPage — Admin POD provider management
// =============================================================================

import { useState, useCallback, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Select,
  Spinner,
  Modal,
  ModalFooter,
} from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Provider {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  capabilities: Record<string, unknown>;
  processingTime: number;
  country: string | null;
  timezone: string | null;
  contactEmail: string | null;
  warehouseCount: number;
  mappingCount: number;
  createdAt: string;
}

interface ProvidersResponse {
  data: Provider[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_CONFIG: Record<string, { label: string; variant: 'default' | 'primary' | 'info' }> = {
  INTERNAL: { label: 'Internal', variant: 'primary' },
  EXTERNAL_API: { label: 'External API', variant: 'info' },
  MANUAL: { label: 'Manual', variant: 'default' },
};

const STATUS_CONFIG: Record<string, { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  INACTIVE: { label: 'Inactive', variant: 'danger' },
  ARCHIVED: { label: 'Archived', variant: 'default' },
  MAINTENANCE: { label: 'Maintenance', variant: 'warning' },
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL_API', label: 'External API' },
  { value: 'MANUAL', label: 'Manual' },
];

const PROVIDER_TYPE_OPTIONS = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL_API', label: 'External API' },
  { value: 'MANUAL', label: 'Manual' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const queryKeys = {
  providers: ['admin', 'providers'] as const,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProvidersPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('INTERNAL');
  const [formCountry, setFormCountry] = useState('');
  const [formTimezone, setFormTimezone] = useState('');
  const [formEmail, setFormEmail] = useState('');

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleStatusChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  }, []);

  const handleTypeChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTypeFilter(e.target.value);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
  }, []);

  const resetForm = useCallback(() => {
    setFormName('');
    setFormType('INTERNAL');
    setFormCountry('');
    setFormTimezone('');
    setFormEmail('');
  }, []);

  // --- Query -----------------------------------------------------------------

  const showArchived = statusFilter === 'ARCHIVED';
  const providersQuery = useQuery<ProvidersResponse, Error>({
    queryKey: [...queryKeys.providers, showArchived ? 'with-archived' : 'no-archived'],
    queryFn: () =>
      api.get<ProvidersResponse>(
        showArchived
          ? '/api/v1/admin/providers?includeArchived=true'
          : '/api/v1/admin/providers',
      ),
  });

  // --- Mutations --------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      type: string;
      country?: string;
      timezone?: string;
      contactEmail?: string;
    }) => api.post('/api/v1/admin/providers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providers });
      setShowCreateModal(false);
      resetForm();
    },
  });

  // --- Derived ---------------------------------------------------------------

  let providers = providersQuery.data?.data ?? [];
  const q = search.trim().toLowerCase();
  if (q) {
    providers = providers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q),
    );
  }
  if (statusFilter) {
    providers = providers.filter((p) => p.status === statusFilter);
  }
  if (typeFilter) {
    providers = providers.filter((p) => p.type === typeFilter);
  }

  const hasFilters = !!(search || statusFilter || typeFilter);

  // --- Loading ---------------------------------------------------------------

  if (providersQuery.isLoading && !providersQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading providers..." />
      </div>
    );
  }

  // --- Error -----------------------------------------------------------------

  if (providersQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            Failed to load providers: {providersQuery.error.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void providersQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormName(e.target.value);
  };

  // --- Render ----------------------------------------------------------------

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="POD Providers"
          description="Manage print-on-demand providers, their warehouses, and product mappings."
          action={
            <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
              Add Provider
            </Button>
          }
        />

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder="Search by name..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <div className="w-40">
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusChange}
            />
          </div>

          <div className="w-40">
            <Select
              options={TYPE_OPTIONS}
              value={typeFilter}
              onChange={handleTypeChange}
            />
          </div>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}

          {providersQuery.isFetching && providersQuery.data && (
            <Spinner size="sm" />
          )}
        </div>

        {/* Table */}
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {providers.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              {hasFilters ? 'No providers match your filters.' : 'No providers found.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-text-secondary">Warehouses</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-text-secondary">Mappings</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {providers.map((provider) => {
                  const typeCfg = TYPE_CONFIG[provider.type] ?? { label: provider.type, variant: 'default' as const };
                  const statusCfg = STATUS_CONFIG[provider.status] ?? { label: provider.status, variant: 'default' as const };

                  return (
                    <tr key={provider.id} className="transition-colors hover:bg-surface-secondary">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link
                          to={`/providers/${provider.id}`}
                          className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
                        >
                          {provider.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={typeCfg.variant} size="sm">{typeCfg.label}</Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={statusCfg.variant} size="sm" dot>{statusCfg.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap text-center">
                        {provider.warehouseCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap text-center">
                        {provider.mappingCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                        {provider.country ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                        {formatDate(provider.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <Link to={`/providers/${provider.id}`}>
                          <Button variant="outline" size="sm">View</Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Create Provider Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetForm(); }}
        title="Add Provider"
        description="Create a new POD provider."
        size="md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate({
              name: formName,
              type: formType,
              country: formCountry || undefined,
              timezone: formTimezone || undefined,
              contactEmail: formEmail || undefined,
            });
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={formName}
            onChange={handleNameChange}
            required
          />
          <Select
            label="Type"
            options={PROVIDER_TYPE_OPTIONS}
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Country"
              value={formCountry}
              onChange={(e) => setFormCountry(e.target.value)}
              placeholder="US"
            />
            <Input
              label="Timezone"
              value={formTimezone}
              onChange={(e) => setFormTimezone(e.target.value)}
              placeholder="America/New_York"
            />
          </div>
          <Input
            label="Contact Email"
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
          />

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
              {createMutation.error?.message ?? 'Failed to create provider.'}
            </div>
          )}

          <ModalFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => { setShowCreateModal(false); resetForm(); }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={createMutation.isPending}
              disabled={!formName.trim()}
            >
              Create Provider
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
